import {useCallback, useEffect, useRef, useState} from 'react';

import {useToast} from '@kit';

import {getApolloError} from '../../../../components/utils';
import {useCreateCharacterSheetMutation, useUpdateCharacterSheetMutation} from '../../../../generated/types';
import {CharacterState, CharacterStatePatch, createCharacterState} from '../model/character-state';
import {withDeathSavesReset} from '../model/death-saves';
import {migrateState} from '../model/state-migrations';

const SAVE_DELAY = 600;
const SAVE_TIMEOUT = 15000;
const ERROR_TOAST_TIMEOUT = 10000;

/** запрос, на который сервер не ответил за отведённое время, считается неудачным */
function withTimeout<T>(request: Promise<T>): Promise<T> {
	return new Promise((resolve, reject) => {
		const timer = setTimeout(() => reject(new Error('Сервер не отвечает')), SAVE_TIMEOUT);
		request.then(resolve, reject).finally(() => clearTimeout(timer));
	});
}

/** pending — изменения ждут отправки, saving — отправляются */
export type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

export const hasUnsavedChanges = (status: SaveStatus) =>
	status === 'pending' || status === 'saving' || status === 'error';

type Options = {
	/** лист уже есть в базе */
	sheetId?: string;
	initialState?: CharacterState;
	/** лист создан в базе после первого изменения */
	onCreated?(sheetId: string): void;
};

export function useSheetEditor({sheetId: initialSheetId, initialState, onCreated}: Options) {
	const [state, setState] = useState<CharacterState>(() =>
		migrateState({...createCharacterState(), ...initialState}),
	);
	const [sheetId, setSheetId] = useState(initialSheetId);
	const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
	const [createSheet] = useCreateCharacterSheetMutation();
	const [updateSheet] = useUpdateCharacterSheetMutation();
	const toast = useToast();

	const stateRef = useRef(state);
	const sheetIdRef = useRef(sheetId);
	const onCreatedRef = useRef(onCreated);
	const isChanged = useRef(false);
	const isSaving = useRef(false);
	const isFailureShown = useRef(false);
	const saveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	stateRef.current = state;
	onCreatedRef.current = onCreated;

	const save = useCallback(async () => {
		if (isSaving.current || !isChanged.current) return;
		isSaving.current = true;
		isChanged.current = false;
		setSaveStatus('saving');
		const data = stateRef.current;
		let isFailed = false;
		try {
			if (sheetIdRef.current) {
				const result = await withTimeout(updateSheet({variables: {id: sheetIdRef.current, data}}));
				if (!result.data?.updateCharacterSheet) {
					throw result.error || new Error('Сервер не подтвердил сохранение');
				}
			} else {
				const result = await withTimeout(createSheet({variables: {data}}));
				const createdId = result.data?.createCharacterSheet.id;
				if (!createdId) throw result.error || new Error('Сервер не подтвердил сохранение');
				sheetIdRef.current = createdId;
				setSheetId(createdId);
				onCreatedRef.current?.(createdId);
			}
			isFailureShown.current = false;
			setSaveStatus(isChanged.current ? 'pending' : 'saved');
		} catch (error) {
			isFailed = true;
			isChanged.current = true;
			setSaveStatus('error');
			if (!isFailureShown.current) {
				isFailureShown.current = true;
				toast.show({
					intent: 'error',
					title: 'Бланк не сохранён',
					body: `${
						getApolloError(error instanceof Error ? error : null).message
					}. Изменения остались на странице, сохранение повторится при следующем изменении.`,
					timeout: ERROR_TOAST_TIMEOUT,
				});
			}
		} finally {
			isSaving.current = false;
		}
		if (isChanged.current && !isFailed) save();
	}, [createSheet, updateSheet, toast]);

	const scheduleSave = useCallback(() => {
		isChanged.current = true;
		if (!isSaving.current) setSaveStatus('pending');
		clearTimeout(saveTimer.current);
		saveTimer.current = setTimeout(save, SAVE_DELAY);
	}, [save]);

	useEffect(() => {
		if (!hasUnsavedChanges(saveStatus)) return;
		const warn = (event: BeforeUnloadEvent) => {
			event.preventDefault();
			event.returnValue = '';
		};
		window.addEventListener('beforeunload', warn);
		return () => window.removeEventListener('beforeunload', warn);
	}, [saveStatus]);

	useEffect(
		() => () => {
			clearTimeout(saveTimer.current);
			save();
		},
		[save],
	);

	const update = useCallback(
		(patch: CharacterStatePatch) => {
			setState((current) =>
				withDeathSavesReset(current, {...current, ...(typeof patch === 'function' ? patch(current) : patch)}),
			);
			scheduleSave();
		},
		[scheduleSave],
	);

	const setChoice = useCallback(
		(key: string, values: string[]) => {
			setState((current) => {
				const {[key]: _previous, ...choices} = current.choices;
				return {...current, choices: values.length ? {...choices, [key]: values} : choices};
			});
			scheduleSave();
		},
		[scheduleSave],
	);

	return {state, sheetId, saveStatus, update, setChoice};
}
